import { useMemo, useState } from "react";
import { Alert, Box, Button, Chip, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ChoiceCard from "./ChoiceCard";
import DocumentReviewDialog from "./DocumentReviewDialog";
import { BusyOverlay, StepActions, StepHeading, primaryButtonSx } from "./EnrollmentShell";
import {
  ConsentDocument,
  CoreSignChoices,
  CoreSignResult,
  EnrollmentError,
  EnrollmentReview,
  enrollmentApi,
  newRequestKey,
} from "./enrollmentApi";

interface ReviewAndSignProps {
  review: EnrollmentReview;
  onSigned: (result: CoreSignResult) => void;
  /** The snapshot went stale (expired, superseded, price or document changed): review again. */
  onStale: (message: string) => void;
  onChangePlan: () => void;
}

const EMPTY: CoreSignChoices = {
  clinicalConsentAccepted: false,
  membershipAccepted: false,
  recurringPaymentAuthorized: false,
  termsAccepted: false,
  nppReceiptChoice: null,
  patientLegalNameConfirmed: false,
  signingIntentConfirmed: false,
};

const KEY_STORE = "enrollment.coreSignKey";

/**
 * One idempotency key per (review, exact choices). A retry of the same submission — a double click,
 * a dropped response — reuses it and gets the original result; changing any choice is a different
 * request and gets a new key, rather than a refusal for "same key, different body".
 */
function requestKeyFor(reviewId: string, choices: CoreSignChoices): string {
  const fingerprint = `${reviewId}|${JSON.stringify(choices)}`;
  try {
    const stored = JSON.parse(sessionStorage.getItem(KEY_STORE) ?? "null") as { fingerprint: string; key: string } | null;
    if (stored?.fingerprint === fingerprint) return stored.key;
    const key = newRequestKey();
    sessionStorage.setItem(KEY_STORE, JSON.stringify({ fingerprint, key }));
    return key;
  } catch {
    return newRequestKey();
  }
}

/** Card order and wording follow the package's Review & Sign layout (spec §3). */
const CARD_COPY: Record<string, string> = {
  "clinical-consent": "How asynchronous and video telehealth care works, its risks and limits, and your responsibilities.",
  "membership-agreement": "Your membership terms, fees, renewal, cancellation and refunds. The Practice countersigns it.",
  "recurring-payment": "The recurring charge you authorize separately from your signature.",
  "patient-notices": "Patient notices, including your Notice of Privacy Practices acknowledgment choice.",
  npp: "How your health information may be used and shared, and your rights.",
  "privacy-policy": "How the website and portal handle your data.",
  "terms-of-use": "The rules for using your account and the portal.",
  "membership-policy": "Cancellation and refund details for your membership.",
};

/**
 * Review & Sign (package spec §3–§4): document cards with full-text dialogs, separate unchecked
 * choices, an NPP acknowledge-or-decline that never blocks, a confirmed signing name and the intent
 * statement, then one Sign & Submit.
 */
export default function ReviewAndSign({ review, onSigned, onStale, onChangePlan }: ReviewAndSignProps) {
  const [choices, setChoices] = useState<CoreSignChoices>(EMPTY);
  const [open, setOpen] = useState<ConsentDocument | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof CoreSignChoices>(key: K, value: CoreSignChoices[K]) =>
    setChoices((current) => ({ ...current, [key]: value }));

  const byId = useMemo(() => Object.fromEntries(review.documents.map((d) => [d.documentId, d])), [review.documents]);

  // Every unmet REQUIRED item, named — the button stays enabled so the patient is told what is
  // missing, rather than facing a greyed-out button with no explanation.
  const missing = [
    !choices.clinicalConsentAccepted && "accept the Consent to Treatment and Telehealth",
    !choices.membershipAccepted && "accept the Membership Agreement",
    !choices.recurringPaymentAuthorized && "authorize the recurring payment",
    !choices.termsAccepted && "accept the Terms of Use",
    !choices.nppReceiptChoice && "choose whether to acknowledge the Notice of Privacy Practices",
    !choices.patientLegalNameConfirmed && "confirm your legal name",
    !choices.signingIntentConfirmed && "confirm you intend to sign",
  ].filter(Boolean) as string[];

  const [showMissing, setShowMissing] = useState(false);

  const submit = async () => {
    if (missing.length > 0) {
      setShowMissing(true);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await enrollmentApi.coreSign(
        requestKeyFor(review.reviewSnapshotId, choices),
        review.reviewSnapshotId,
        choices,
      );
      onSigned(result);
    } catch (e) {
      const err = e as EnrollmentError;
      if (err.code === "REVIEW_STALE") onStale(err.message);
      else setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  /** One document: its title, a one-line summary, Read, and the choice that belongs to it. */
  const docCard = (documentId: string, extra?: React.ReactNode) => {
    const doc = byId[documentId];
    if (!doc) return null;
    return (
      <Box
        key={documentId}
        sx={{ border: 1, borderColor: "divider", borderRadius: 3, bgcolor: "background.paper", overflow: "hidden" }}
      >
        <Stack direction="row" spacing={1.5} sx={{ p: 2, alignItems: "flex-start" }}>
          <Box
            sx={(theme) => ({
              width: 40,
              height: 40,
              borderRadius: 2,
              flexShrink: 0,
              display: "grid",
              placeItems: "center",
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: "primary.dark",
            })}
          >
            <DescriptionOutlinedIcon aria-hidden />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
              <Typography component="h3" sx={{ fontWeight: 700 }}>
                {doc.title}
              </Typography>
              {doc.isTestContent && <Chip size="small" color="warning" variant="outlined" label="TEST" />}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {CARD_COPY[documentId]}
            </Typography>
            <Button
              size="small"
              onClick={() => setOpen(doc)}
              aria-label={`Read ${doc.title}`}
              sx={{ px: 0, mt: 0.5, textTransform: "none", fontWeight: 600 }}
            >
              Read the full document
            </Button>
          </Box>
        </Stack>
        {extra && <Box sx={{ px: 2, pb: 2 }}>{extra}</Box>}
      </Box>
    );
  };

  const check = (key: keyof CoreSignChoices, label: React.ReactNode) => (
    <ChoiceCard
      kind="checkbox"
      selected={choices[key] === true}
      onClick={() => set(key, !choices[key] as never)}
      label={label}
    />
  );

  const textLink = (label: string, doc?: ConsentDocument) =>
    doc && (
      <Button size="small" onClick={() => setOpen(doc)} sx={{ px: 0, mt: 1, textTransform: "none" }}>
        {label}
      </Button>
    );

  return (
    <Box>
      <StepHeading
        title="Review & sign"
        subtitle="Read each document, make your choices, and sign once at the bottom. Nothing is pre-selected, and signed copies go to My Documents → Consents & Agreements."
      />

      <Box
        sx={(theme) => ({
          mb: 3,
          p: 2.5,
          borderRadius: 3,
          color: "primary.contrastText",
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
        })}
      >
        <Typography variant="overline" sx={{ opacity: 0.9, letterSpacing: 1 }}>
          Your membership
        </Typography>
        <Typography variant="h5" component="p" sx={{ fontWeight: 700 }}>
          {review.planLabel} · {review.chargeToday}
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.95, mt: 0.5 }}>
          Renews at {review.renewalAmount} {review.billingInterval} · starts {review.membershipStartDate}, paid through{" "}
          {review.paidThroughDate}, next renewal {review.nextRenewalDate}
        </Typography>
        <Button
          size="small"
          onClick={onChangePlan}
          sx={{ mt: 1, px: 0, color: "inherit", textDecoration: "underline", textTransform: "none" }}
        >
          Change plan
        </Button>
      </Box>

      <Stack spacing={2}>
        {docCard(
          "clinical-consent",
          check("clinicalConsentAccepted", "I have read and accept the Consent to Treatment and Telehealth."),
        )}
        {docCard(
          "membership-agreement",
          <>
            {check("membershipAccepted", "I have read and accept the Membership Agreement and Financial Responsibility.")}
            {textLink("Read the Membership Cancellation and Refund Policy", byId["membership-policy"])}
          </>,
        )}
        {docCard("recurring-payment", check("recurringPaymentAuthorized", review.recurringAuthorizationText))}
        {docCard(
          "terms-of-use",
          <>
            {check("termsAccepted", "I accept the Terms of Use.")}
            {textLink("Read the Privacy Policy", byId["privacy-policy"])}
          </>,
        )}
        {docCard(
          "patient-notices",
          <Box role="radiogroup" aria-labelledby="npp-choice-label">
            <Typography id="npp-choice-label" variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Notice of Privacy Practices — either choice lets you continue.
            </Typography>
            <Stack spacing={1}>
              <ChoiceCard
                selected={choices.nppReceiptChoice === "acknowledged"}
                onClick={() => set("nppReceiptChoice", "acknowledged")}
                label="I acknowledge that I received the Notice of Privacy Practices."
              />
              <ChoiceCard
                selected={choices.nppReceiptChoice === "declined_acknowledgment"}
                onClick={() => set("nppReceiptChoice", "declined_acknowledgment")}
                label="I decline to acknowledge receipt."
                description="The notice has still been provided to you."
              />
            </Stack>
            {textLink("Read the Notice of Privacy Practices", byId.npp)}
          </Box>,
        )}
      </Stack>

      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
          Your signature
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {review.intentText}
        </Typography>
        <Box
          sx={{
            mb: 2,
            px: 2,
            py: 1.5,
            borderRadius: 2,
            border: 1,
            borderStyle: "dashed",
            borderColor: "text.disabled",
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Signing as
          </Typography>
          <Typography sx={{ fontFamily: "'Brush Script MT', 'Segoe Script', cursive", fontSize: "1.75rem", lineHeight: 1.2 }}>
            {review.signingName}
          </Typography>
        </Box>
        <Stack spacing={1}>
          {check("patientLegalNameConfirmed", "This is my full legal name. (If it is wrong, contact us before signing.)")}
          {check("signingIntentConfirmed", "I intend to sign the Clinical Consent and Membership Agreement electronically.")}
        </Stack>
      </Box>

      {showMissing && missing.length > 0 && (
        <Alert severity="warning" sx={{ mt: 2 }} role="alert">
          Before signing, please {missing.join("; ")}.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }} role="alert">
          {error}
        </Alert>
      )}

      <StepActions>
        <Button variant="contained" size="large" fullWidth sx={primaryButtonSx} onClick={submit} disabled={submitting}>
          {submitting ? "Signing…" : "Sign & Submit"}
        </Button>
      </StepActions>

      <DocumentReviewDialog document={open} onClose={() => setOpen(null)} />
      <BusyOverlay open={submitting} message="Signing your documents…" />
    </Box>
  );
}
